import { scopeThreadRef } from "@t3tools/client-runtime/environment";
import type { EnvironmentId, ScopedThreadRef, ThreadId } from "@t3tools/contracts";

import { DraftId, useComposerDraftStore } from "./composerDraftStore";
import { buildThreadRouteParams } from "./threadRoutes";

const LAST_CHAT_ROUTE_STORAGE_KEY = "morrow:last-chat-route";

export type LastChatRoute =
  | {
      kind: "server";
      threadRef: ScopedThreadRef;
    }
  | {
      kind: "draft";
      draftId: DraftId;
      threadRef: ScopedThreadRef;
    };

export type LastChatNavigateTarget =
  | {
      to: "/$environmentId/$threadId";
      params: {
        environmentId: EnvironmentId;
        threadId: ThreadId;
      };
    }
  | {
      to: "/draft/$draftId";
      params: {
        draftId: DraftId;
      };
    }
  | {
      to: "/";
    };

type StoredLastChatRoute = {
  kind: "server" | "draft";
  environmentId: string;
  threadId: string;
  draftId?: string;
};

let lastChatRoute: LastChatRoute | null = readStoredLastChatRoute();

export function rememberLastChatRoute(route: LastChatRoute): void {
  lastChatRoute = route;
  persistLastChatRoute(route);
}

export function readLastChatRoute(): LastChatRoute | null {
  return lastChatRoute;
}

export function resetLastChatRoute(): void {
  lastChatRoute = null;
  try {
    window.sessionStorage.removeItem(LAST_CHAT_ROUTE_STORAGE_KEY);
  } catch {
    // Ignore quota/storage errors; in-memory state is enough for this session.
  }
}

/**
 * Workspace pages (Team, Agents, …) used to send Back to `/`, which mints a
 * fresh empty draft. Restore the thread the user actually left.
 */
export function resolveLastChatNavigation(
  route: LastChatRoute | null,
  lookup: {
    draftExists: (draftId: DraftId) => boolean;
  },
): LastChatNavigateTarget {
  if (!route) {
    return { to: "/" };
  }
  if (route.kind === "draft" && lookup.draftExists(route.draftId)) {
    return {
      to: "/draft/$draftId",
      params: { draftId: route.draftId },
    };
  }
  return {
    to: "/$environmentId/$threadId",
    params: buildThreadRouteParams(route.threadRef),
  };
}

export function readLastChatNavigation(): LastChatNavigateTarget {
  return resolveLastChatNavigation(readLastChatRoute(), {
    draftExists: (draftId) => useComposerDraftStore.getState().getDraftSession(draftId) !== null,
  });
}

function persistLastChatRoute(route: LastChatRoute): void {
  try {
    const stored: StoredLastChatRoute = {
      kind: route.kind,
      environmentId: route.threadRef.environmentId,
      threadId: route.threadRef.threadId,
      ...(route.kind === "draft" ? { draftId: route.draftId } : {}),
    };
    window.sessionStorage.setItem(LAST_CHAT_ROUTE_STORAGE_KEY, JSON.stringify(stored));
  } catch {
    // Ignore quota/storage errors; in-memory state is enough for this session.
  }
}

function readStoredLastChatRoute(): LastChatRoute | null {
  try {
    const raw = window.sessionStorage.getItem(LAST_CHAT_ROUTE_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    return parseStoredLastChatRoute(JSON.parse(raw));
  } catch {
    return null;
  }
}

export function parseStoredLastChatRoute(value: unknown): LastChatRoute | null {
  if (!value || typeof value !== "object") {
    return null;
  }
  const stored = value as Partial<StoredLastChatRoute>;
  if (
    (stored.kind !== "server" && stored.kind !== "draft") ||
    typeof stored.environmentId !== "string" ||
    stored.environmentId.length === 0 ||
    typeof stored.threadId !== "string" ||
    stored.threadId.length === 0
  ) {
    return null;
  }
  const threadRef = scopeThreadRef(
    stored.environmentId as EnvironmentId,
    stored.threadId as ThreadId,
  );
  if (stored.kind === "draft") {
    if (typeof stored.draftId !== "string" || stored.draftId.length === 0) {
      return null;
    }
    return {
      kind: "draft",
      draftId: DraftId.make(stored.draftId),
      threadRef,
    };
  }
  return {
    kind: "server",
    threadRef,
  };
}
